import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap">
              CSSSpreadsheet<span className="text-primary-500">.pics</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">Home</a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 w-56 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="#brands" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">Brands</a>
            <a href="#guides" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">Guides</a>
            <a href="#faq" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">FAQ</a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              CSSBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-1">
          <a href="/" className="block py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap">Home</a>
          <p className="py-2.5 text-sm font-semibold text-foreground-500 whitespace-nowrap">Categories</p>
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 pl-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
              {cat.name}
            </a>
          ))}
          <a href="#brands" className="block py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap">Brands</a>
          <a href="#guides" className="block py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap">Guides</a>
          <a href="#faq" className="block py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap">FAQ</a>
          <div className="flex gap-3 pt-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 text-center px-4 py-2.5 text-sm font-medium border border-foreground-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              CSSBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 text-center px-4 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}